import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
    return new Date(iso).toLocaleString();
}

const TYPES = [
    {
        id: 'database',
        icon: '🗄️',
        titleTr: 'Veritabanı Yedeği',
        titleEn: 'Database Backup',
        descTr: 'Tüm sensör verileri, tarlalar, kullanıcılar ve ayarlar. PostgreSQL .sql dosyası.',
        descEn: 'All sensor readings, fields, users and settings. PostgreSQL .sql file.',
        ext: '.sql',
        color: '#1e40af',
        bg: '#dbeafe',
    },
    {
        id: 'site',
        icon: '🌐',
        titleTr: 'Site Yedeği',
        titleEn: 'Site Backup',
        descTr: 'Backend kaynak kodu (src/, prisma/) ve frontend build dosyaları. .tar.gz',
        descEn: 'Backend source (src/, prisma/) and frontend build files. .tar.gz',
        ext: '.tar.gz',
        color: '#065f46',
        bg: '#d1fae5',
    },
    {
        id: 'full',
        icon: '📦',
        titleTr: 'Tam Yedek',
        titleEn: 'Full Backup',
        descTr: 'Veritabanı + Site — hepsi tek .tar.gz dosyasında.',
        descEn: 'Database + Site — everything in one .tar.gz file.',
        ext: '.tar.gz',
        color: '#6b21a8',
        bg: '#f3e8ff',
    },
];

export default function BackupManager() {
    const { language } = useLanguage();
    const tr = language === 'tr';

    const [loading, setLoading] = useState({}); // { database: true, site: false, full: false }
    const [progress, setProgress] = useState({}); // { database: 'Preparing…' }
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(true);

    const fetchFiles = useCallback(async () => {
        setFilesLoading(true);
        try {
            const res = await fetch(`${API_URL}/backup/status`, { headers: getHeaders() });
            if (res.ok) {
                const data = await res.json();
                setFiles(data.files || []);
            }
        } catch {}
        setFilesLoading(false);
    }, []);

    useEffect(() => { fetchFiles(); }, [fetchFiles]);

    const handleBackup = async (type) => {
        setError(null);
        setSuccess(null);
        setLoading(prev => ({ ...prev, [type.id]: true }));
        setProgress(prev => ({ ...prev, [type.id]: tr ? 'Hazırlanıyor…' : 'Preparing…' }));

        try {
            const res = await fetch(`${API_URL}/backup/${type.id}`, {
                method: 'POST',
                headers: getHeaders(),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || err.detail || `HTTP ${res.status}`);
            }

            setProgress(prev => ({ ...prev, [type.id]: tr ? 'İndiriliyor…' : 'Downloading…' }));

            // Get filename from header or construct one
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename="([^"]+)"/);
            const filename = match ? match[1] : `swamed-${type.id}-backup${type.ext}`;

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSuccess(tr
                ? `✅ ${type.titleTr} başarıyla indirildi: ${filename}`
                : `✅ ${type.titleEn} downloaded successfully: ${filename}`);
            fetchFiles();
        } catch (e) {
            setError((tr ? `${type.titleTr} başarısız: ` : `${type.titleEn} failed: `) + e.message);
        } finally {
            setLoading(prev => ({ ...prev, [type.id]: false }));
            setProgress(prev => ({ ...prev, [type.id]: null }));
        }
    };

    const handleDelete = async (filename) => {
        if (!window.confirm(tr ? `"${filename}" silinsin mi?` : `Delete "${filename}"?`)) return;
        try {
            await fetch(`${API_URL}/backup/${encodeURIComponent(filename)}`, {
                method: 'DELETE',
                headers: getHeaders(),
            });
            fetchFiles();
        } catch (e) {
            setError(e.message);
        }
    };

    const isAnyLoading = Object.values(loading).some(Boolean);

    return (
        <div style={{ padding: '24px 0', maxWidth: 860 }}>
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
                <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>
                    💾 {tr ? 'Yedekleme Merkezi' : 'Backup Center'}
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
                    {tr
                        ? 'Veritabanı ve site dosyalarını yerel bilgisayarınıza indirin. Sunucuda son 5 yedek saklanır.'
                        : 'Download database and site files to your computer. Last 5 backups are kept on the server.'}
                </p>
            </div>

            {/* Alerts */}
            {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#991b1b', fontSize: 13 }}>
                    ❌ {error}
                </div>
            )}
            {success && (
                <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#065f46', fontSize: 13 }}>
                    {success}
                </div>
            )}

            {/* Backup type cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 36 }}>
                {TYPES.map(type => {
                    const isLoading = loading[type.id];
                    const prog = progress[type.id];
                    return (
                        <div key={type.id} style={{
                            background: '#fff',
                            border: `2px solid ${isLoading ? type.color : '#e5e7eb'}`,
                            borderRadius: 14,
                            padding: 22,
                            boxShadow: isLoading ? `0 0 0 3px ${type.bg}` : '0 1px 4px rgba(0,0,0,0.06)',
                            transition: 'all 0.2s',
                        }}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>{type.icon}</div>
                            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                                {tr ? type.titleTr : type.titleEn}
                            </div>
                            <p style={{ color: '#6b7280', fontSize: 12.5, lineHeight: 1.5, margin: '0 0 16px' }}>
                                {tr ? type.descTr : type.descEn}
                            </p>

                            {isLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{
                                        width: 18, height: 18, borderRadius: '50%',
                                        border: `2px solid ${type.color}`,
                                        borderTopColor: 'transparent',
                                        animation: 'spin 0.8s linear infinite',
                                        flexShrink: 0,
                                    }} />
                                    <span style={{ fontSize: 13, color: type.color, fontWeight: 600 }}>{prog}</span>
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleBackup(type)}
                                    disabled={isAnyLoading}
                                    style={{
                                        background: type.color,
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '10px 18px',
                                        cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                                        fontWeight: 700,
                                        fontSize: 13,
                                        opacity: isAnyLoading ? 0.6 : 1,
                                        width: '100%',
                                        transition: 'opacity 0.15s',
                                    }}
                                >
                                    ⬇ {tr ? 'İndir' : 'Download'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Info box */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px', marginBottom: 28 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#334155' }}>
                    ℹ️ {tr ? 'Yedekleme Hakkında' : 'About Backups'}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#64748b', lineHeight: 2 }}>
                    <li>{tr ? 'Veritabanı yedeği tüm sensör okumalarını ve tarihsel verileri içerir.' : 'Database backup includes all sensor readings and historical data.'}</li>
                    <li>{tr ? 'Site yedeği kaynak kodunu içerir; node_modules dahil değildir.' : 'Site backup contains source code; node_modules are excluded.'}</li>
                    <li>{tr ? 'Tam yedek için süre sensör verisi miktarına göre 1–3 dakika sürebilir.' : 'Full backup may take 1–3 minutes depending on sensor data volume.'}</li>
                    <li>{tr ? 'Sunucuda her türden en fazla 5 yedek saklanır; eskiler otomatik silinir.' : 'Server keeps max 5 backups of each type; older ones are auto-deleted.'}</li>
                    <li>{tr ? 'Geri yükleme için sunucuya psql ile bağlanıp .sql dosyasını yükleyin.' : 'To restore: connect to server via psql and load the .sql file.'}</li>
                </ul>
            </div>

            {/* Existing backup files on server */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                        🗂 {tr ? 'Sunucudaki Yedekler' : 'Backups on Server'}
                    </div>
                    <button onClick={fetchFiles} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#374151' }}>
                        ↻ {tr ? 'Yenile' : 'Refresh'}
                    </button>
                </div>

                {filesLoading ? (
                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                        {tr ? 'Yükleniyor…' : 'Loading…'}
                    </div>
                ) : files.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
                        {tr ? 'Sunucuda kayıtlı yedek yok. Yukarıdan bir yedek indirin.' : 'No backups stored on server yet. Download one above.'}
                    </div>
                ) : (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead style={{ background: '#1e293b', color: '#94a3b8' }}>
                                <tr>
                                    <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600 }}>{tr ? 'Dosya Adı' : 'Filename'}</th>
                                    <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 600 }}>{tr ? 'Boyut' : 'Size'}</th>
                                    <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600 }}>{tr ? 'Oluşturulma' : 'Created'}</th>
                                    <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 600 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((f, i) => {
                                    const isDb = f.name.includes('-db-') || f.name.endsWith('.sql');
                                    const isFull = f.name.includes('-full-');
                                    const icon = isFull ? '📦' : isDb ? '🗄️' : '🌐';
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <td style={{ padding: '10px 14px' }}>
                                                <span style={{ marginRight: 8 }}>{icon}</span>
                                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.name}</span>
                                            </td>
                                            <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                                                {fmtSize(f.size)}
                                            </td>
                                            <td style={{ padding: '10px 14px', color: '#64748b' }}>
                                                {fmtDate(f.createdAt)}
                                            </td>
                                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleDelete(f.name)}
                                                    title={tr ? 'Sil' : 'Delete'}
                                                    style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#991b1b', fontSize: 12, fontWeight: 600 }}>
                                                    🗑
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Spinner animation */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
